import React from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { usePermission, usePermissions } from "hooks/usePermissions";
import { useAuth } from "hooks/useAuth";
import { ROUTES } from "constants/index";
import { PageId, ActionCode } from "types/index";
import { Spinner } from "components/ui";

interface PermissionRouteProps {
  children: React.ReactNode;
  pageId: PageId;
  actionCode: ActionCode;
  fallbackPath?: string;
  entityPath?: string;
}

function PermissionLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function usePermissionsGate() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: permissionsLoading, hasLoaded } = usePermissions();
  const waiting =
    authLoading || permissionsLoading || (isAuthenticated && !hasLoaded);
  return { isAuthenticated, waiting };
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  pageId,
  actionCode,
  fallbackPath = ROUTES.DASHBOARD,
  entityPath,
}) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, waiting } = usePermissionsGate();
  const hasPermission = usePermission(pageId, actionCode);
  const canView = usePermission(pageId, ActionCode.VIEW);

  if (waiting) return <PermissionLoading />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;

  if (!hasPermission) {
    if (actionCode === ActionCode.EDIT && canView && entityPath && id) {
      const qs = searchParams.toString();
      return (
        <Navigate
          to={`${entityPath}/${id}/view${qs ? `?${qs}` : ""}`}
          replace
        />
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

interface CompanyFormPermissionRouteProps {
  children: React.ReactNode;
  actionCode: ActionCode;
}

export const CompanyFormPermissionRoute: React.FC<
  CompanyFormPermissionRouteProps
> = ({ children, actionCode }) => {
  const [searchParams] = useSearchParams();
  const isIndividual =
    searchParams.get("accountType") === ROUTES.ACCOUNT_TYPE_INDIVIDUAL;

  return (
    <PermissionRoute
      pageId={isIndividual ? PageId.INDIVIDUALS : PageId.COMPANIES}
      actionCode={actionCode}
      fallbackPath={isIndividual ? ROUTES.INDIVIDUALS : ROUTES.COMPANIES}
      entityPath={ROUTES.COMPANIES}
    >
      {children}
    </PermissionRoute>
  );
};

interface CompanyProfileRouteProps {
  children: React.ReactNode;
}

export const CompanyProfileRoute: React.FC<CompanyProfileRouteProps> = ({
  children,
}) => {
  const { waiting } = usePermissionsGate();
  const hasPermission = usePermission(PageId.COMPANY_PROFILE, ActionCode.VIEW);

  if (waiting) return <PermissionLoading />;
  if (!hasPermission) return <Navigate to={ROUTES.DASHBOARD} replace />;
  return <>{children}</>;
};
